/**
 * Wishlist Header Badge Manager
 * 
 * ■ 責務
 * - ヘッダーのお気に入りバッジ（件数）を一元管理
 * - updateWishlistBadges(count) を唯一の描画口として全ページで使用
 * - PC/SP複数バッジを querySelectorAll で全更新
 * 
 * ■ ルール
 * - toggle/削除は Optimistic UI（即時反映）を原則とし、API結果のcountで後追い整合
 * - バッジ要素は複数存在し得るため、必ず querySelectorAll で全更新
 */
(function() {
  "use strict";

  // ===== LocalStorage キー =====
  var WISHLIST_ITEMS_KEY = "omni_wishlist_items";
  var COUNT_CACHE_KEY = "omni_wishlist_count_cache_v1";

  // ===== ユーティリティ =====
  function safeParse(json, fallback) {
    try {
      return JSON.parse(json);
    } catch (e) {
      return fallback;
    }
  }

  /**
   * countキャッシュをLocalStorageに保存
   * @param {number} count
   */
  function saveCachedCount(count) {
    try {
      var cache = {
        count: count,
        ts: Date.now()
      };
      localStorage.setItem(COUNT_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.warn("[wishlist-header] Failed to save count cache", e);
    }
  }

  /**
   * countキャッシュをLocalStorageから取得
   * @returns {number|null}
   */
  function getCachedCount() {
    try {
      var raw = localStorage.getItem(COUNT_CACHE_KEY);
      if (!raw) return null;
      
      var cache = safeParse(raw, null);
      if (!cache || typeof cache.count !== "number") return null;
      
      // キャッシュは24時間有効（古すぎる場合は無視）
      var age = Date.now() - (cache.ts || 0);
      if (age > 24 * 60 * 60 * 1000) return null;
      
      return cache.count;
    } catch (e) {
      console.warn("[wishlist-header] Failed to get count cache", e);
      return null;
    }
  }

  /**
   * ヘッダーのWishlistバッジを更新（全バッジ対象）
   * @param {number|string} count - 表示する件数
   */
  function updateWishlistBadges(count) {
    // countを正規化（NaN対策）
    var num = Number(count);
    if (isNaN(num) || num < 0) {
      num = 0;
    }

    // 全バッジを取得（PC/SP両対応）
    var badges = document.querySelectorAll("[data-wishlist-count-badge]");
    var values = document.querySelectorAll("[data-wishlist-count-value]");
    var srValues = document.querySelectorAll("[data-wishlist-count-value-sr]");

    // 件数テキストを更新
    values.forEach(function(el) {
      el.textContent = String(num);
    });

    // スクリーンリーダー用テキストを更新
    srValues.forEach(function(el) {
      el.textContent = String(num);
    });

    // バッジの表示/非表示を制御
    badges.forEach(function(badge) {
      if (num > 0) {
        badge.hidden = false;
        badge.style.display = "";
      } else {
        badge.hidden = true;
        badge.style.display = "none";
      }
    });

    // ★ countキャッシュをLocalStorageに保存（ページ跨ぎで即時整合）
    saveCachedCount(num);

    console.log("[wishlist-header] badge updated:", num);
  }

  /**
   * 初期件数を取得
   * @returns {Promise<number>}
   */
  async function getInitialCount() {
    // window.OMNI.wishlist.getCount() があればそれを使用
    if (window.OMNI && window.OMNI.wishlist && typeof window.OMNI.wishlist.getCount === "function") {
      try {
        var count = await window.OMNI.wishlist.getCount();
        console.log("[wishlist-header] getCount() returned:", count);
        return Number(count) || 0;
      } catch (e) {
        console.warn("[wishlist-header] getCount() failed:", e);
      }
    }

    // フォールバック: LocalStorageから直接読む
    try {
      var raw = localStorage.getItem(WISHLIST_ITEMS_KEY);
      if (!raw) return 0;

      var data = safeParse(raw, { items: [] });
      var items = Array.isArray(data.items) ? data.items : [];
      console.log("[wishlist-header] fallback count from localStorage:", items.length);
      return items.length;
    } catch (e) {
      console.warn("[wishlist-header] localStorage fallback failed:", e);
      return 0;
    }
  }

  /**
   * イベントハンドラ: omni:wishlist:count
   */
  function handleCountEvent(e) {
    var count = e && e.detail && e.detail.count;
    console.log("[wishlist-header] received omni:wishlist:count event:", count);
    updateWishlistBadges(count);
  }

  /**
   * イベントハンドラ: omni:wishlist:count-change
   */
  function handleCountChangeEvent(e) {
    var count = e && e.detail && e.detail.count;
    console.log("[wishlist-header] received omni:wishlist:count-change event:", count);
    updateWishlistBadges(count);
  }

  /**
   * 初期化（キャッシュ優先 → サーバー確定値で後追い整合）
   */
  async function init() {
    console.log("[wishlist-header] initializing...");

    // イベント購読
    window.addEventListener("omni:wishlist:count", handleCountEvent);
    window.addEventListener("omni:wishlist:count-change", handleCountChangeEvent);

    // ★ 1. まずキャッシュがあれば即座に表示（ページ跨ぎ整合）
    var cachedCount = getCachedCount();
    if (cachedCount !== null) {
      console.log("[wishlist-header] using cached count:", cachedCount);
      // キャッシュからの表示（saveCachedCountは呼ばない）
      var badges = document.querySelectorAll("[data-wishlist-count-badge]");
      var values = document.querySelectorAll("[data-wishlist-count-value]");
      var srValues = document.querySelectorAll("[data-wishlist-count-value-sr]");
      
      values.forEach(function(el) { el.textContent = String(cachedCount); });
      srValues.forEach(function(el) { el.textContent = String(cachedCount); });
      badges.forEach(function(badge) {
        if (cachedCount > 0) {
          badge.hidden = false;
          badge.style.display = "";
        } else {
          badge.hidden = true;
          badge.style.display = "none";
        }
      });
    }

    // ★ 2. その後、サーバー/LocalStorageから確定値を取得して後追い整合
    var serverCount = await getInitialCount();
    if (serverCount !== cachedCount) {
      console.log("[wishlist-header] server count differs from cache, updating:", serverCount);
      updateWishlistBadges(serverCount); // これでキャッシュも更新される
    } else {
      console.log("[wishlist-header] server count matches cache:", serverCount);
      // 念のためキャッシュを再保存（タイムスタンプ更新）
      saveCachedCount(serverCount);
    }

    console.log("[wishlist-header] initialized with count:", serverCount);
  }

  /**
   * pageshow イベントで bfcache 復帰時も確実に初期化
   */
  function handlePageShow(e) {
    // bfcache から復帰した場合
    if (e.persisted) {
      console.log("[wishlist-header] pageshow (bfcache), re-syncing...");
      var cachedCount = getCachedCount();
      if (cachedCount !== null) {
        // キャッシュからの表示（DOMがそのまま残っているので念のため再描画）
        var badges = document.querySelectorAll("[data-wishlist-count-badge]");
        var values = document.querySelectorAll("[data-wishlist-count-value]");
        var srValues = document.querySelectorAll("[data-wishlist-count-value-sr]");
        
        values.forEach(function(el) { el.textContent = String(cachedCount); });
        srValues.forEach(function(el) { el.textContent = String(cachedCount); });
        badges.forEach(function(badge) {
          if (cachedCount > 0) {
            badge.hidden = false;
            badge.style.display = "";
          } else {
            badge.hidden = true;
            badge.style.display = "none";
          }
        });
      }
    }
  }

  // グローバル公開
  window.updateWishlistBadges = updateWishlistBadges;

  // DOMContentLoaded で初期化
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    // すでにDOMが読み込まれている場合は即時実行
    init();
  }

  // pageshow イベントで bfcache 復帰時も確実に初期化
  window.addEventListener("pageshow", handlePageShow);
})();

